import { memo } from 'react';

interface ProgressBarProps {
  value: number | null | undefined;
  max?: number;
  showPercent?: boolean;
  color?: string;
  size?: 'sm' | 'md' | 'lg';
}

const SIZE_CONFIG = {
  sm: { height: '4px', fontSize: '10px', gap: '6px' },
  md: { height: '6px', fontSize: '11px', gap: '8px' },
  lg: { height: '8px', fontSize: '12px', gap: '10px' }
};

export const ProgressBar = memo(({
  value,
  max = 100,
  showPercent = true,
  color,
  size = 'md'
}: ProgressBarProps) => {
  if (value === null || value === undefined) {
    return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
  }

  const percentage = Math.min(100, Math.max(0, (value / max) * 100));
  const config = SIZE_CONFIG[size];

  // Auto color based on percentage if not provided
  const barColor = color ||
    (percentage >= 80 ? 'var(--color-positive)' :
     percentage >= 50 ? 'var(--color-primary)' :
     percentage >= 25 ? 'var(--color-warning-500)' :
     'var(--color-negative)');

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: config.gap,
        minWidth: '100px'
      }}
    >
      {/* Progress bar container */}
      <div
        style={{
          flex: 1,
          height: config.height,
          background: 'var(--color-neutral-100)',
          borderRadius: 'var(--radius-full)',
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            width: `${percentage}%`,
            height: '100%',
            background: barColor,
            borderRadius: 'var(--radius-full)',
            transition: 'width 0.3s ease'
          }}
        />
      </div>

      {/* Percentage display */}
      {showPercent && (
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: config.fontSize,
            fontWeight: 500,
            color: 'var(--color-text-secondary)',
            minWidth: '32px',
            textAlign: 'right'
          }}
        >
          {percentage.toFixed(0)}%
        </span>
      )}
    </div>
  );
});

ProgressBar.displayName = 'ProgressBar';
